"use client";

import { useEffect, useMemo, useRef, useState, type DragEvent } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RiDeleteBinLine } from "react-icons/ri";
import { MdOutlineContentCopy, MdVisibility } from "react-icons/md";
import { IoClose, IoCheckmark } from "react-icons/io5";
import mainAPI from "../../services/main/mainAPI";
import sequenceAPI from "../../services/sequence/sequenceAPI";
import ConfirmPopUp from "../popup/confirmPopUp";
import StatusPopUp from "../popup/statusPopUp";
import { formatDateShort } from "../../utils/formatDatetime";

type SequenceProps = {
    createdGameData?: {
        id: number | string;
        exercise_name?: string;
        uuid?: string;
    } | null;
};

type SequenceGameItem = {
    id: number | string;
    uuid: string;
    name: string;
    thumbnail: string;
    subject: string;
    level: string;
    gameTypeKey: string;
    gameTypeLabel: string;
    createdAt: string;
};

function buildThumbnailUrl(fileName: unknown) {
    const name = String(fileName ?? "").trim();
    if (!name) return "";
    if (name.startsWith("http://") || name.startsWith("https://")) return name;
    const baseUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "https://thaipilacreate.gforcesolution.com";
    return `${baseUrl.replace(/\/$/, "")}/api/${encodeURIComponent(name)}`;
}

function getTeacherUuidFromLocalStorage() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return "";
        const parsed = JSON.parse(raw) as { id?: string };
        return typeof parsed?.id === "string" ? parsed.id.trim() : "";
    } catch {
        return "";
    }
}

const GAME_TYPE_LABEL: Record<string, string> = {
    find_the_match: "Find the match",
    game_show_quiz: "Game show Quiz",
    flip_cards: "Flip cards",
    "air_plan/flying_fruits": "Air plan Flying Fruits",
    flappy_bird: "Flappy Bird",
    whack_a_mole: "Whack a mole",
    anagram: "Anagram",
    complete_the_sentence: "Complete the Sentence",
    situation: "Situation",
};

const PREVIEW_BASE = "https://thaipilagame.gforcesolution.com";

function mapRow(item: unknown, i: number): SequenceGameItem {
    const row = item as Record<string, unknown>;
    const uuid = String(row.uuid ?? "").trim();
    const parsedId = Number(row.id);
    const gameTypeKey = String(row.game_type ?? "").trim();
    const rawDate = String(row.create_at ?? row.created_at ?? "").trim();
    return {
        id: Number.isFinite(parsedId) ? parsedId : uuid || `game_${i + 1}`,
        uuid,
        name: String(row.exercise_name ?? "-").trim() || "-",
        thumbnail: buildThumbnailUrl(row.thumbnail),
        subject: String(row.subject ?? "").trim(),
        level: String(row.level_class ?? row.levelClass ?? row.level ?? "").trim(),
        gameTypeKey,
        gameTypeLabel: GAME_TYPE_LABEL[gameTypeKey] ?? gameTypeKey,
        createdAt: rawDate ? formatDateShort(rawDate) : "-",
    };
}

// ── Row component (shared between both panels) ─────────────────────────────
type GameRowProps = {
    game: SequenceGameItem;
    side: "left" | "right";
    draggingId: string;
    onDragStart: (e: DragEvent<HTMLDivElement>, id: string) => void;
    onDragEnd: () => void;
    onDrop?: (e: DragEvent<HTMLDivElement>, targetId: string) => void;
    onDelete?: (id: string) => void;
    onPreview?: (game: SequenceGameItem) => void;
    onCopyUuid?: (uuid: string, name: string) => void;
    copiedId?: string | null;
    copyToast?: { open: boolean; message: string; type: "success" | "error"; rowId: string | null };
};

function GameRow({ game, side, onDragStart, onDragEnd, onDrop, onDelete, onPreview, onCopyUuid, copiedId, copyToast }: GameRowProps) {
    return (
        <div
            draggable={side === "left"}
            onDragStart={(e) => onDragStart(e, String(game.id))}
            onDragEnd={onDragEnd}
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop ? (e) => { e.preventDefault(); e.stopPropagation(); onDrop(e, String(game.id)); } : undefined}
            className={`grid items-center gap-2 rounded-lg px-3 py-2.5 text-sm ${
                side === "left"
                    ? "cursor-grab bg-neutral-50 hover:bg-neutral-100 grid-cols-[1fr_52px_80px_90px_28px]"
                    : "cursor-pointer bg-neutral-50 hover:bg-[#f0faff] grid-cols-[1fr_52px_80px_90px_80px_52px]"
            }`}
            onClick={side === "right" && onPreview ? () => onPreview(game) : undefined}
        >
            <span className="truncate font-medium text-neutral-800">{game.name}</span>
            <span className="text-center text-xs text-neutral-500">{game.level || "-"}</span>
            <span className="truncate text-xs text-neutral-500">{game.subject || "-"}</span>
            <span className="text-xs text-neutral-400">{game.createdAt}</span>
            {side === "right" ? (
                <span>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onPreview?.(game); }}
                        className="cursor-pointer rounded-md bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-[#22A8DF] hover:text-white"
                    >
                        ดูตัวอย่าง
                    </button>
                </span>
            ) : null}
            <span className="flex items-center justify-end gap-1.5">
                {side === "left" ? (
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDelete?.(String(game.id)); }}
                        className="cursor-pointer text-neutral-400 hover:text-rose-500"
                    >
                        <RiDeleteBinLine className="text-sm" />
                    </button>
                ) : (
                    <div className="relative">
                        {copyToast?.open && copyToast.rowId === game.uuid ? (
                            <div
                                className={`pointer-events-none absolute -top-11 left-1/2 z-20 w-max max-w-[360px] -translate-x-1/2 rounded-md px-2 py-1 text-[11px] leading-4 text-white shadow ${copyToast.type === "success" ? "bg-emerald-600" : "bg-rose-600"}`}
                                style={{ animation: "copyBubbleSlideFromRight 2200ms ease-in-out forwards" }}
                            >
                                {copyToast.message}
                            </div>
                        ) : null}
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onCopyUuid?.(game.uuid, game.name); }}
                            className="cursor-pointer text-neutral-400 hover:text-sky-600"
                            title="Copy UUID"
                        >
                            {copiedId === game.uuid ? (
                                <IoCheckmark className="text-sm text-emerald-600" />
                            ) : (
                                <MdOutlineContentCopy className="text-sm" />
                            )}
                        </button>
                    </div>
                )}
            </span>
        </div>
    );
}


// ── Main component ─────────────────────────────────────────────────────────
export default function Sequence({ createdGameData }: SequenceProps) {
    const [sequenceName, setSequenceName] = useState("");
    const initialSequenceNameRef = useRef("");
    const [selectedGames, setSelectedGames] = useState<SequenceGameItem[]>([]);
    const [allGames, setAllGames] = useState<SequenceGameItem[]>([]);
    const [savedGameIdKeys, setSavedGameIdKeys] = useState<string[]>([]);
    const [draggingId, setDraggingId] = useState("");
    const [isLoadingGames, setIsLoadingGames] = useState(false);
    const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [statusPopUp, setStatusPopUp] = useState<{ title: string; message: string; type: "info" | "success" | "error" } | null>(null);
    const [previewGame, setPreviewGame] = useState<SequenceGameItem | null>(null);
    const [sequencePreviewOpen, setSequencePreviewOpen] = useState(false);
    const [addByCodeOpen, setAddByCodeOpen] = useState(false);
    const [addByCodeInput, setAddByCodeInput] = useState("");
    const [addByCodeResult, setAddByCodeResult] = useState<SequenceGameItem | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [copyToast, setCopyToast] = useState<{ open: boolean; message: string; type: "success" | "error"; rowId: string | null }>({ open: false, message: "", type: "success", rowId: null });
    const copyToastTimerRef = useRef<number | null>(null);

    // filters
    const [filterSearch, setFilterSearch] = useState("");
    const [filterDateFrom, setFilterDateFrom] = useState<Date | null>(null);
    const [filterDateTo, setFilterDateTo] = useState<Date | null>(null);
    const [filterGameType, setFilterGameType] = useState("ประเภทเกม");
    const [filterLevel, setFilterLevel] = useState("ชั้นเรียน");
    const [filterSubject, setFilterSubject] = useState("วิชา");

    useEffect(() => {
        const initial = createdGameData?.exercise_name?.trim() || "";
        setSequenceName(initial);
        initialSequenceNameRef.current = initial;
    }, [createdGameData?.exercise_name]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setIsLoadingGames(true);
            const teacherUuid = getTeacherUuidFromLocalStorage();
            if (!teacherUuid) { setAllGames([]); setIsLoadingGames(false); return; }
            const response = await mainAPI.getAllLessonLibraryByTeacherUuid(teacherUuid);
            if (!mounted) return;
            if (!response || (response as { status?: string }).status === "failed") { setAllGames([]); setIsLoadingGames(false); return; }
            const rows = Array.isArray(response) ? response
                : Array.isArray((response as { data?: unknown[] }).data) ? (response as { data: unknown[] }).data : [];
            const filtered = rows.filter((item) => {
                const row = item as Record<string, unknown>;
                const source = String(row.source ?? "").toLowerCase();
                const deleted = row.delete_at ?? row.deleteAt ?? null;
                return source === "game" && (deleted === null || deleted === undefined || String(deleted).trim() === "");
            });
            setAllGames(filtered.map(mapRow));
            setIsLoadingGames(false);
        };
        void load();
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        const uuid = String(createdGameData?.uuid ?? "").trim();
        if (!uuid) { setSelectedGames([]); setSavedGameIdKeys([]); return; }
        setSavedGameIdKeys([]);
        const loadSelected = async () => {
            const response = await sequenceAPI.getGameInSequenceByUuid(uuid);
            if (!mounted) return;
            if (!response || (response as { status?: string }).status === "failed") { setSelectedGames([]); setSavedGameIdKeys([]); return; }
            const games = Array.isArray((response as { games?: unknown[] }).games) ? (response as { games: unknown[] }).games : [];
            const mapped = games
                .filter((item) => { const g = item as Record<string, unknown>; const del = g.delete_at ?? g.deleteAt ?? null; return del === null || del === undefined || String(del).trim() === ""; })
                .map(mapRow);
            setSelectedGames(mapped);
            setSavedGameIdKeys(mapped.map((g) => String(g.id)));
        };
        void loadSelected();
        return () => { mounted = false; };
    }, [createdGameData?.uuid]);

    const selectedIdSet = useMemo(() => new Set(selectedGames.map((g) => String(g.id))), [selectedGames]);
    const availableGames = useMemo(() => allGames.filter((g) => !selectedIdSet.has(String(g.id))), [allGames, selectedIdSet]);

    const filteredAvailableGames = useMemo(() => {
        const kw = filterSearch.trim().toLowerCase();
        return availableGames.filter((g) => {
            if (kw && !g.name.toLowerCase().includes(kw) && !g.subject.toLowerCase().includes(kw)) return false;
            if (filterGameType !== "ประเภทเกม" && g.gameTypeLabel !== filterGameType) return false;
            if (filterLevel !== "ชั้นเรียน" && g.level !== filterLevel) return false;
            if (filterSubject !== "วิชา" && g.subject !== filterSubject) return false;
            if (filterDateFrom || filterDateTo) {
                const parts = g.createdAt.split("/");
                if (parts.length === 3) {
                    const gameDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                    if (filterDateFrom) { const from = new Date(filterDateFrom); from.setHours(0,0,0,0); if (gameDate < from) return false; }
                    if (filterDateTo) { const to = new Date(filterDateTo); to.setHours(23,59,59,999); if (gameDate > to) return false; }
                }
            }
            return true;
        });
    }, [availableGames, filterSearch, filterGameType, filterLevel, filterSubject, filterDateFrom, filterDateTo]);

    const availableGameTypes = useMemo(() => [...new Set(availableGames.map((g) => g.gameTypeLabel).filter(Boolean))], [availableGames]);
    const availableLevels = useMemo(() => [...new Set(availableGames.map((g) => g.level).filter(Boolean))], [availableGames]);
    const availableSubjects = useMemo(() => [...new Set(availableGames.map((g) => g.subject).filter(Boolean))], [availableGames]);

    const pendingSaveGames = useMemo(() => { const saved = new Set(savedGameIdKeys); return selectedGames.filter((g) => !saved.has(String(g.id))); }, [selectedGames, savedGameIdKeys]);
    const pendingDeleteGameIdKeys = useMemo(() => selectedGames.filter((g) => !selectedIdSet.has(String(g.id))).map(() => "").filter(Boolean), [selectedGames, selectedIdSet]);
    const pendingDeleteIds = useMemo(() => savedGameIdKeys.filter((id) => !selectedIdSet.has(id)), [savedGameIdKeys, selectedIdSet]);

    const trimmedName = sequenceName.trim();
    const hasPendingNameUpdate = Boolean(createdGameData?.id) && trimmedName !== initialSequenceNameRef.current;
    const canSave = pendingSaveGames.length > 0 || pendingDeleteIds.length > 0 || hasPendingNameUpdate;

    const moveToSelected = (gameId: string) => {
        const target = allGames.find((g) => String(g.id) === gameId);
        if (!target || selectedIdSet.has(gameId)) return;
        setSelectedGames((prev) => [...prev, target]);
    };

    const reorder = (dragId: string, dropId: string) => {
        if (!dragId || !dropId || dragId === dropId) return;
        setSelectedGames((prev) => {
            const dragIdx = prev.findIndex((g) => String(g.id) === dragId);
            const dropIdx = prev.findIndex((g) => String(g.id) === dropId);
            if (dropIdx < 0) return prev;
            if (dragIdx < 0) {
                const target = allGames.find((g) => String(g.id) === dragId);
                if (!target || prev.some((g) => String(g.id) === dragId)) return prev;
                const next = [...prev]; next.splice(dropIdx, 0, target); return next;
            }
            const next = [...prev]; const [moved] = next.splice(dragIdx, 1); next.splice(dropIdx, 0, moved); return next;
        });
    };

    const handleConfirmSave = async () => {
        if (!canSave || !createdGameData?.id) { setIsConfirmSaveOpen(false); return; }
        setIsSaving(true);
        if (hasPendingNameUpdate) {
            if (!trimmedName) { setIsSaving(false); setIsConfirmSaveOpen(false); setStatusPopUp({ title: "ไม่สำเร็จ", message: "กรุณากรอกชื่อ Sequence", type: "error" }); return; }
            const res = await sequenceAPI.updateExcerciseName(createdGameData.id, trimmedName);
            if (!res || (res as { status?: string }).status === "failed") { setIsSaving(false); setIsConfirmSaveOpen(false); setStatusPopUp({ title: "ไม่สำเร็จ", message: "อัปเดตชื่อไม่สำเร็จ", type: "error" }); return; }
            initialSequenceNameRef.current = trimmedName;
        }
        let nextSaved = [...savedGameIdKeys];
        if (pendingSaveGames.length > 0) {
            const res = await sequenceAPI.addGameToSequence(pendingSaveGames.map((g) => ({ sequenceId: createdGameData.id, gameId: g.id })));
            if (!res || (res as { status?: string }).status === "failed") { setIsSaving(false); setIsConfirmSaveOpen(false); setStatusPopUp({ title: "ไม่สำเร็จ", message: "บันทึก Sequence ไม่สำเร็จ", type: "error" }); return; }
            nextSaved = Array.from(new Set([...nextSaved, ...pendingSaveGames.map((g) => String(g.id))]));
        }
        if (pendingDeleteIds.length > 0) {
            const res = await sequenceAPI.hardDeleteGameInSequence({ sequenceId: createdGameData.id, gameIds: pendingDeleteIds });
            if (!res || (res as { status?: string }).status === "failed") { setSavedGameIdKeys(nextSaved); setIsSaving(false); setIsConfirmSaveOpen(false); setStatusPopUp({ title: "ไม่สำเร็จ", message: "ลบเกมไม่สำเร็จ", type: "error" }); return; }
            const delSet = new Set(pendingDeleteIds); nextSaved = nextSaved.filter((id) => !delSet.has(id));
        }
        setSavedGameIdKeys(nextSaved); setIsSaving(false); setIsConfirmSaveOpen(false);
        setStatusPopUp({ title: "สำเร็จ", message: "บันทึก Sequence สำเร็จ", type: "success" });
    };

    const previewUrl = previewGame ? `${PREVIEW_BASE}/${encodeURIComponent(previewGame.uuid)}` : null;
    const sequencePreviewUrl = createdGameData?.uuid ? `${PREVIEW_BASE}/${encodeURIComponent(createdGameData.uuid)}` : null;
    const isSaved = Boolean(createdGameData?.uuid) && !canSave;

    const showCopyToast = (rowId: string, message: string, type: "success" | "error") => {
        setCopyToast({ open: true, message, type, rowId });
        if (copyToastTimerRef.current) window.clearTimeout(copyToastTimerRef.current);
        copyToastTimerRef.current = window.setTimeout(() => {
            setCopyToast((prev) => ({ ...prev, open: false, rowId: null }));
            copyToastTimerRef.current = null;
        }, 2200);
    };

    const handleCopyUuid = (uuid: string, gameName?: string) => {
        const label = gameName ? gameName : uuid;
        const doCopy = (text: string) => {
            if (navigator.clipboard?.writeText) {
                navigator.clipboard.writeText(text).then(() => {
                    setCopiedId(uuid);
                    setTimeout(() => setCopiedId(null), 1500);
                    showCopyToast(uuid, `คัดลอก ${label} แล้ว`, "success");
                }).catch(() => showCopyToast(uuid, "คัดลอก UUID ไม่สำเร็จ", "error"));
            } else {
                try {
                    const ta = document.createElement("textarea");
                    ta.value = text;
                    ta.style.position = "fixed";
                    ta.style.opacity = "0";
                    document.body.appendChild(ta);
                    ta.focus();
                    ta.select();
                    document.execCommand("copy");
                    document.body.removeChild(ta);
                    setCopiedId(uuid);
                    setTimeout(() => setCopiedId(null), 1500);
                    showCopyToast(uuid, `คัดลอก ${label} แล้ว`, "success");
                } catch {
                    showCopyToast(uuid, "คัดลอก UUID ไม่สำเร็จ", "error");
                }
            }
        };
        doCopy(uuid);
    };

    return (
        <>
        <section className="grid h-full grid-cols-2 gap-4">

            {/* ── LEFT PANEL ── */}
            <div className="flex flex-col rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
                {/* name input */}
                <p className="mb-1.5 text-sm font-semibold text-neutral-600">ชื่อชุดแบบฝึกหัด</p>
                <div className="flex gap-2">
                    <input
                        value={sequenceName}
                        onChange={(e) => setSequenceName(e.target.value)}
                        placeholder="ชื่อชุดแบบฝึกหัด"
                        className="flex-1 rounded-lg border border-neutral-300 bg-neutral-100 px-3 py-2 text-sm text-neutral-800 outline-none focus:border-[#22A8DF] focus:bg-white"
                    />
                    <button
                        type="button"
                        disabled={!isSaved}
                        onClick={() => { if (isSaved) setSequencePreviewOpen(true); }}
                        title={!isSaved ? "กรุณาบันทึก Sequence ก่อนดูตัวอย่าง" : "ดูตัวอย่าง"}
                        className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold shadow-[2px_2px_0_#b8a800] transition-opacity ${isSaved ? "cursor-pointer bg-[#FDEF5C] text-[#2B2B2B] hover:brightness-97" : "cursor-not-allowed bg-[#FDEF5C] text-[#2B2B2B] opacity-40"}`}
                    >
                        <MdVisibility className="text-base" />
                        ดูตัวอย่าง
                    </button>
                </div>

                {/* add by code button */}
                <div className="mt-3 flex justify-end">
                    <button
                        type="button"
                        onClick={() => { setAddByCodeInput(""); setAddByCodeResult(null); setAddByCodeOpen(true); }}
                        className="cursor-pointer rounded-lg bg-[#22A8DF] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#1f97c8]"
                    >
                        + เพิ่มโดยรหัส
                    </button>
                </div>

                {/* selected games */}
                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain") || draggingId; if (id) { moveToSelected(id); setDraggingId(""); } }}
                    className="flex-1 overflow-y-auto"
                >
                    {selectedGames.length === 0 ? (
                        <div className="flex h-20 items-center justify-center rounded-lg border-2 border-dashed border-neutral-200 text-sm text-neutral-400">
                            ลากเกมจากฝั่งขวา หรือเพิ่มโดยรหัส
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {selectedGames.map((game) => (
                                <GameRow
                                    key={String(game.id)}
                                    game={game}
                                    side="left"
                                    draggingId={draggingId}
                                    onDragStart={(e, id) => { e.dataTransfer.setData("text/plain", id); setDraggingId(id); }}
                                    onDragEnd={() => setDraggingId("")}
                                    onDrop={(e, targetId) => { reorder(e.dataTransfer.getData("text/plain") || draggingId, targetId); setDraggingId(""); }}
                                    onDelete={(id) => setSelectedGames((prev) => prev.filter((g) => String(g.id) !== id))}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* save button */}
                <div className="mt-4 flex justify-center">
                    <button
                        type="button"
                        onClick={() => { if (canSave && !isSaving) setIsConfirmSaveOpen(true); }}
                        disabled={!canSave || isSaving}
                        className={`rounded-lg px-10 py-2 text-sm font-semibold transition-colors ${!canSave || isSaving ? "cursor-not-allowed bg-neutral-300 text-neutral-500" : "cursor-pointer bg-[#22A8DF] text-white hover:bg-[#1f97c8]"}`}
                    >
                        {isSaving ? "กำลังบันทึก..." : "บันทึก"}
                    </button>
                </div>
            </div>

            {/* ── RIGHT PANEL ── */}
            <div className="flex flex-col rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm">
                {/* filter row 1 */}
                <div className="grid grid-cols-2 gap-2">
                    <input
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                        placeholder="ค้นหา"
                        className="rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 text-sm outline-none focus:border-[#22A8DF] focus:bg-white"
                    />
                    <DatePicker
                        selectsRange
                        startDate={filterDateFrom ?? undefined}
                        endDate={filterDateTo ?? undefined}
                        onChange={([start, end]) => { setFilterDateFrom(start ?? null); setFilterDateTo(end ?? null); }}
                        isClearable
                        placeholderText="ช่วงเวลา"
                        dateFormat="dd/MM/yyyy"
                        className="w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 py-2 text-sm text-neutral-600 outline-none focus:border-[#22A8DF]"
                        wrapperClassName="w-full"
                    />
                </div>
                {/* filter row 2 */}
                <div className="mt-2 grid grid-cols-3 gap-2">
                    <select value={filterGameType} onChange={(e) => setFilterGameType(e.target.value)} className="rounded-lg border border-neutral-300 bg-neutral-50 px-2 py-2 text-sm text-neutral-600 outline-none focus:border-[#22A8DF]">
                        <option value="ประเภทเกม">ประเภทเกม</option>
                        {availableGameTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)} className="rounded-lg border border-neutral-300 bg-neutral-50 px-2 py-2 text-sm text-neutral-600 outline-none focus:border-[#22A8DF]">
                        <option value="ชั้นเรียน">ชั้นเรียน</option>
                        {availableLevels.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                    <select value={filterSubject} onChange={(e) => setFilterSubject(e.target.value)} className="rounded-lg border border-neutral-300 bg-neutral-50 px-2 py-2 text-sm text-neutral-600 outline-none focus:border-[#22A8DF]">
                        <option value="วิชา">วิชา</option>
                        {availableSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                {/* game list */}
                <div className="mt-3 flex-1 overflow-y-auto">
                    {isLoadingGames ? (
                        <div className="flex h-20 items-center justify-center text-sm text-neutral-400">กำลังโหลดข้อมูล...</div>
                    ) : filteredAvailableGames.length === 0 ? (
                        <div className="flex h-20 items-center justify-center text-sm text-neutral-400">ไม่พบข้อมูล</div>
                    ) : (
                        <div className="space-y-2">
                            {filteredAvailableGames.map((game) => (
                                <GameRow
                                    key={String(game.id)}
                                    game={game}
                                    side="right"
                                    draggingId={draggingId}
                                    onDragStart={(e, id) => { e.dataTransfer.setData("text/plain", id); setDraggingId(id); }}
                                    onDragEnd={() => setDraggingId("")}
                                    onPreview={(g) => setPreviewGame(g)}
                                    onCopyUuid={handleCopyUuid}
                                    copiedId={copiedId}
                                    copyToast={copyToast}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── ADD BY CODE POPUP ── */}
            {addByCodeOpen ? (
                <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-[420px] rounded-2xl bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.18)]">
                        {/* header */}
                        <div className="flex items-start justify-between">
                            <div>
                                <h3 className="text-base font-bold text-neutral-800">เพิ่มแบบฝึกหัดด้วยรหัส</h3>
                                <p className="mt-1 text-[13px] leading-5 text-neutral-500">
                                    กรอกรหัสแบบฝึกหัดที่ต้องการเพิ่ม เพื่อค้นหาและนำรายการนั้น<br />มาเพิ่มในแบบฝึกหัดนี้
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setAddByCodeOpen(false)}
                                className="ml-3 cursor-pointer text-neutral-400 hover:text-neutral-600"
                            >
                                <IoClose className="text-xl" />
                            </button>
                        </div>

                        {/* input */}
                        <div className="mt-4">
                            <label className="mb-1.5 block text-sm font-semibold text-neutral-700">รหัสแบบฝึกหัด</label>
                            <input
                                type="text"
                                value={addByCodeInput}
                                onChange={(e) => {
                                    const val = e.target.value.trim();
                                    setAddByCodeInput(e.target.value);
                                    const found = allGames.find((g) => g.uuid === val) ?? null;
                                    setAddByCodeResult(found);
                                }}
                                placeholder="วาง UUID ที่นี่"
                                className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#22A8DF]"
                            />
                        </div>

                        {/* result */}
                        <div className="mt-4">
                            <label className="mb-1.5 block text-sm font-semibold text-neutral-700">พบรายการ</label>
                            <div className="min-h-[64px] rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2.5">
                                {addByCodeResult ? (
                                    <div>
                                        <p className="font-semibold text-neutral-800">{addByCodeResult.name}</p>
                                        <p className="mt-0.5 text-[13px] text-neutral-500">
                                            {[addByCodeResult.level, addByCodeResult.subject].filter(Boolean).join(" - ")}
                                            {addByCodeInput ? ` - ${addByCodeInput.trim().slice(0, 6)}...` : ""}
                                        </p>
                                    </div>
                                ) : addByCodeInput.trim() ? (
                                    <p className="text-sm text-neutral-400">ไม่พบรายการที่ตรงกัน</p>
                                ) : (
                                    <p className="text-sm text-neutral-300">—</p>
                                )}
                            </div>
                        </div>

                        {/* actions */}
                        <div className="mt-5 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setAddByCodeOpen(false)}
                                className="cursor-pointer px-4 py-2 text-sm font-semibold text-[#22A8DF] hover:underline"
                            >
                                ยกเลิก
                            </button>
                            <button
                                type="button"
                                disabled={!addByCodeResult}
                                onClick={() => {
                                    if (!addByCodeResult) return;
                                    moveToSelected(String(addByCodeResult.id));
                                    setAddByCodeOpen(false);
                                }}
                                className={`rounded-lg px-5 py-2 text-sm font-semibold text-white transition-colors ${addByCodeResult ? "cursor-pointer bg-[#22A8DF] hover:bg-[#1f97c8]" : "cursor-not-allowed bg-neutral-300"}`}
                            >
                                เพิ่มรายการนี้
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {/* ── SEQUENCE PREVIEW MODAL ── */}
            {sequencePreviewOpen && sequencePreviewUrl ? (
                <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/50 p-4">
                    <div className="relative h-[90vh] w-full max-w-400 overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
                        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
                            <h3 className="text-base font-semibold text-neutral-800">Preview</h3>
                            <button
                                type="button"
                                onClick={() => setSequencePreviewOpen(false)}
                                className="cursor-pointer rounded-lg px-3 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-100"
                            >
                                ปิด
                            </button>
                        </div>
                        <iframe
                            src={sequencePreviewUrl}
                            title="Sequence Preview"
                            className="h-[calc(100%-52px)] w-full border-0"
                            allowFullScreen
                        />
                    </div>
                </div>
            ) : null}

            {/* ── PREVIEW MODAL ── */}
            {previewGame && previewUrl ? (
                <div className="fixed inset-0 z-70 flex items-center justify-center bg-black/50 p-4">
                    <div className="relative h-[90vh] w-full max-w-400 overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
                        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
                            <h3 className="text-base font-semibold text-neutral-800">Preview</h3>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => { moveToSelected(String(previewGame.id)); setPreviewGame(null); }}
                                    className="cursor-pointer rounded-lg bg-[#22A8DF] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#1f97c8]"
                                >
                                    + เพิ่มในชุด
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setPreviewGame(null)}
                                    className="cursor-pointer rounded-lg px-3 py-1.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-100"
                                >
                                    ปิด
                                </button>
                            </div>
                        </div>
                        <iframe
                            src={previewUrl}
                            title="Game Preview"
                            className="h-[calc(100%-52px)] w-full border-0"
                            allowFullScreen
                        />
                    </div>
                </div>
            ) : null}

            {isConfirmSaveOpen ? (
                <ConfirmPopUp
                    title="ยืนยันการบันทึก"
                    message="ต้องการบันทึก Sequence นี้หรือไม่"
                    cancelText="ยกเลิก"
                    confirmText="ยืนยัน"
                    onCancel={() => { if (!isSaving) setIsConfirmSaveOpen(false); }}
                    onConfirm={() => { void handleConfirmSave(); }}
                    isConfirming={isSaving}
                />
            ) : null}

            {statusPopUp ? (
                <StatusPopUp
                    title={statusPopUp.title}
                    message={statusPopUp.message}
                    type={statusPopUp.type}
                    onClose={() => setStatusPopUp(null)}
                />
            ) : null}
        </section>

        <style jsx>{`
            @keyframes copyBubbleSlideFromRight {
                0% {
                    opacity: 0;
                    transform: translateX(calc(-50% + 16px)) translateY(0);
                }
                12% {
                    opacity: 1;
                    transform: translateX(-50%) translateY(0);
                }
                78% {
                    opacity: 1;
                    transform: translateX(-50%) translateY(0);
                }
                100% {
                    opacity: 0;
                    transform: translateX(-50%) translateY(0);
                }
            }
        `}</style>
        </>
    );
}
